import React, { useMemo } from 'react';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { ErrorMessage, Field, FormFooter, FormSection } from '@atlaskit/form';
import TextArea from '@atlaskit/textarea';
import { ApprovalEntity, ApprovalType } from '@css-xp/apd-common';
import './approval-form-section.scss';
import {
  ApprovalWarningForInvoiceDate,
  ApprovalWarningForOrderDiscount,
  ApprovalWarningForPricingPlanAndDiscount,
  ApprovalWarningForPricingPlanChange,
  ApprovalWarningForProductDeletion,
  ApprovalWarningForQuoteDiscount,
  ApprovalWarningForQuoteExtension,
  ApprovalWarningForSubscriptionRefund,
  ApprovalWarningForSubscriptionStartDate,
  ApprovalWarningForTaxRefund,
  ApprovalWarningForTrialExtension,
} from './approval-warnings/approval-warnings';

export interface ApprovalFormSectionProps {
  approvalType: ApprovalType;
  slackChannel?: string;
  onCancel: () => void;
  isLoading: boolean;
  isLineItemRefund?: boolean;
  approvalItems?: ApprovalEntity[];
  standardItems?: ApprovalEntity[];
  isInvoiceDateApproval?: boolean;
  onEdit?: (_param: boolean) => void;
  approvalRequiredProcesses?: ApprovalType[];
  isDisabled?: boolean;
  isSubscriptionStartDateApproval?: boolean;
}

const ApprovalFormSection = function ({
  approvalType,
  slackChannel = '',
  onCancel,
  isLoading,
  isLineItemRefund = false,
  approvalItems = [],
  standardItems,
  isInvoiceDateApproval,
  onEdit,
  approvalRequiredProcesses,
  isDisabled = false,
  isSubscriptionStartDateApproval = false,
}: ApprovalFormSectionProps) {
  const isEditableApprovalProcess = useMemo(() => {
    return (
      isLineItemRefund ||
      [
        ApprovalType.TRIAL_EXTENSION,
        ApprovalType.QUOTE_EXTENSION,
        ApprovalType.ORDER_DISCOUNT,
        ApprovalType.PRICING_PLAN_CHANGE,
        ApprovalType.SUBSCRIPTION_REFUND,
      ].includes(approvalType)
    );
  }, [approvalType, isLineItemRefund]);

  const sectionMessage = useMemo(() => {
    if (approvalRequiredProcesses && approvalRequiredProcesses.length) {
      if (
        approvalRequiredProcesses.includes(ApprovalType.PRICING_PLAN_CHANGE) &&
        approvalRequiredProcesses.includes(ApprovalType.ORDER_DISCOUNT)
      ) {
        const reason = approvalItems.length && approvalItems.map((item: ApprovalEntity) => item.reason);
        return <ApprovalWarningForPricingPlanAndDiscount reason={reason as string[]} slackChannel={slackChannel} />;
      }
    }

    switch (approvalType) {
      case ApprovalType.SUBSCRIPTION_REFUND:
        if (isSubscriptionStartDateApproval) {
          return <ApprovalWarningForSubscriptionStartDate slackChannel={slackChannel} />;
        }
        if (isInvoiceDateApproval) {
          return <ApprovalWarningForInvoiceDate slackChannel={slackChannel} />;
        }
        return (
          <ApprovalWarningForSubscriptionRefund
            slackChannel={slackChannel}
            approvalLineItems={approvalItems?.map((item) => item.entityId)}
            standardLineItems={standardItems?.map((item) => item.entityId)}
            isExclusiveLineItemRequest={isLineItemRefund}
          />
        );
      case ApprovalType.TAX_REFUND:
        return <ApprovalWarningForTaxRefund slackChannel={slackChannel} />;
      case ApprovalType.TRIAL_EXTENSION: {
        const entity = approvalItems[0];
        const reason = entity?.reason || '';
        return <ApprovalWarningForTrialExtension reason={reason} slackChannel={slackChannel} />;
      }
      case ApprovalType.ORDER_DISCOUNT: {
        const entity = approvalItems[0];
        const reason = entity?.reason || '';
        return <ApprovalWarningForOrderDiscount reason={reason} slackChannel={slackChannel} />;
      }
      case ApprovalType.PRODUCT_DELETION: {
        const entity = approvalItems[0];
        const reason = entity?.reason || '';
        return <ApprovalWarningForProductDeletion reason={reason} slackChannel={slackChannel} />;
      }
      case ApprovalType.QUOTE_DISCOUNT: {
        const entity = approvalItems[0];
        const reason = entity?.reason || '';
        return <ApprovalWarningForQuoteDiscount reason={reason} slackChannel={slackChannel} />;
      }
      case ApprovalType.QUOTE_EXTENSION: {
        const entity = approvalItems[0];
        const reason = entity?.reason || '';
        return <ApprovalWarningForQuoteExtension reason={reason} slackChannel={slackChannel} />;
      }
      case ApprovalType.PRICING_PLAN_CHANGE: {
        const entity = approvalItems[0];
        const reason = entity?.reason || '';
        return <ApprovalWarningForPricingPlanChange reason={reason} slackChannel={slackChannel} />;
      }
    }
  }, [
    approvalItems,
    approvalType,
    isInvoiceDateApproval,
    isLineItemRefund,
    slackChannel,
    standardItems,
    isSubscriptionStartDateApproval,
  ]);

  return (
    <>
      <FormSection>{sectionMessage}</FormSection>
      {/* <FormSection>
        <Field name="approvalSupportTicket" label="Support ticket link" isRequired>
          {({ fieldProps }) => <TextField {...fieldProps} name="approvalSupportTicket" />}
        </Field>
      </FormSection> */}
      <FormSection>
        <Field<string, HTMLTextAreaElement>
          name="comments"
          label="Comment for approver"
          isRequired={true}
          validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
        >
          {({ fieldProps, error }) => (
            <>
              <TextArea
                {...fieldProps}
                placeholder="Add comment"
                testId="comments-input"
                isRequired={true}
                isInvalid={error ? true : false}
              />
              {error && <ErrorMessage>This field is required</ErrorMessage>}
            </>
          )}
        </Field>
      </FormSection>
      <FormFooter align="end">
        <ButtonGroup>
          <Button appearance="subtle" onClick={() => onCancel()} testId="cancel-btn">
            Cancel
          </Button>
          {isEditableApprovalProcess && onEdit && (
            <Button appearance="subtle" onClick={() => onEdit?.(false)} testId="edit-request-btn">
              Edit request
            </Button>
          )}
          <LoadingButton
            appearance="warning"
            type="submit"
            isLoading={isLoading}
            testId="submit-btn"
            isDisabled={isDisabled}
          >
            Request approval
          </LoadingButton>
        </ButtonGroup>
      </FormFooter>
    </>
  );
};

export default ApprovalFormSection;
